import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { StarIcon, CheckCircleIcon, CreditCardIcon, ArrowRightIcon, HeartIcon, UsersIcon, TicketIcon, GraduationCapIcon, ZapIcon, ShieldCheckIcon, FlowerIcon, MedalIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
function AnimatedCounter({
  target,
  duration = 2000,
  showPlus = true
}: {
  target: number;
  duration?: number;
  showPlus?: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number | null = null;
    let rafId: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);
  return <span data-id="element-4" data-mp="3fww2kga9swuc">
      {showPlus ? '+' : ''}
      {count.toLocaleString('es-AR')}
    </span>;
}
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: React.ReactNode;
};
const Button = ({
  children,
  className = '',
  ...props
}: ButtonProps) => <button className={className} {...props} data-id="element-5" data-mp="3ouy20recvhfi">
    {children}
  </button>;
const PURCHASE_LINK_BASE = 'https://esenciaaurica.store/cart/44400455385130:1';
const PURCHASE_LINK_PREMIUM = 'https://esenciaaurica.store/cart/44499622133802:1';
const OFFERS = {
  base: {
    id: 'base',
    title: 'Esencia Áurica + 6 Bonos Gratuitos',
    price: '19.990',
    oldPrice: '59.990',
    description: 'Aprendé a crear perfumes naturales, Agua Florida y brumas áuricas desde casa con una guía paso a paso.',
    includes: ['Guía Esencia Áurica', '6 Bonos Exclusivos', 'Acceso inmediato', 'Formato PDF'],
    link: PURCHASE_LINK_BASE
  },
  premium: {
    id: 'premium',
    title: 'Pack Completo Esencia Áurica + Cosmética Viva',
    price: '29.990',
    oldPrice: '99.990',
    description: 'La experiencia más completa para aprender a crear perfumes naturales, brumas áuricas, skincare, cremas, maquillaje natural y productos capilares desde casa.',
    includes: ['Esencia Áurica', 'Cosmética Viva', '14 Bonos Exclusivos', 'Acceso inmediato', 'Formato PDF'],
    link: PURCHASE_LINK_PREMIUM
  }
};
const ARG_CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Salta', 'Neuquén', 'Mar del Plata', 'San Miguel de Tucumán', 'La Plata', 'Santa Fe'];
const NAMES = ['María', 'Florencia', 'Lucía', 'Martina', 'Sofía', 'Valentina', 'Camila', 'Julieta', 'Paula', 'Agustina'];
const Lavender = ({
  className = '',
  delay = 0,
  scale = 1,
  rotate = 0
}: {
  className?: string;
  delay?: number;
  scale?: number;
  rotate?: number;
}) => <motion.div className={`absolute pointer-events-none z-0 ${className}`} initial={{
  rotate: rotate - 2
}} animate={{
  rotate: [rotate - 2, rotate + 2, rotate - 2]
}} transition={{
  duration: 5 + Math.random() * 2,
  repeat: Infinity,
  ease: 'easeInOut',
  delay
}} style={{
  transformOrigin: 'bottom center',
  scale
}} data-id="element-6" data-mp="38dwt9r3467wm">
    <svg width="60" height="180" viewBox="0 0 60 180" fill="none" data-id="element-7" data-mp="3rzjfg5xf06g0">
      {/* Stem */}
      <path d="M30 180C30 140 30 100 30 60" stroke="#5a7a4a" strokeWidth="1.5" strokeLinecap="round" opacity="0.8" data-id="element-8" data-mp="3n1i09w340r9t" />
      {/* Flower clusters */}
      {[...Array(10)].map((_, i) => <g key={i} opacity={1 - i * 0.05} data-id="element-9" data-mp="3dbomqpjeq22x" data-mpi={i}>
          <ellipse cx={30 + (i % 2 === 0 ? 3 : -3)} cy={20 + i * 10} rx="3.5" ry="6" fill={i % 3 === 0 ? '#b19cd9' : '#9b7ede'} transform={`rotate(${i % 2 === 0 ? 20 : -20} ${30 + (i % 2 === 0 ? 3 : -3)} ${20 + i * 10})`} data-id="element-10" data-mp="eloizf0yfbrp" />
          <ellipse cx={30 + (i % 2 === 0 ? -2 : 2)} cy={22 + i * 10} rx="2.5" ry="4" fill="#d8bfd8" opacity="0.6" data-id="element-11" data-mp="3ukpkxogmaji7" />
        </g>)}
      {/* Small leaves */}
      <path d="M30 130C20 120 15 100 15 90" stroke="#5a7a4a" strokeWidth="1" strokeLinecap="round" opacity="0.4" data-id="element-12" data-mp="3lk1djclqbhea" />
      <path d="M30 150C40 140 45 120 45 110" stroke="#5a7a4a" strokeWidth="1" strokeLinecap="round" opacity="0.4" data-id="element-13" data-mp="i32h0313q0ix" />
    </svg>
  </motion.div>;
function FaqItem({
  question,
  answer,
  index
}: {
  question: string;
  answer: string;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1
  }} className="border-b border-rose-100/50" data-id="element-14" data-mp="w0r1vav657nz">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left group" data-id="element-15" data-mp="2giiaw8x99dm7">
        <span style={{
        fontFamily: "'Playfair Display', serif"
      }} className="text-lg md:text-xl font-semibold text-gray-800 group-hover:text-[#b8865c] transition-colors duration-300" data-id="element-16" data-mp="2qa61y8pwgsn5">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} className="text-[#d4af37]" data-id="element-17" data-mp="2bl9twbnuffmj">
          <ArrowRightIcon className="w-5 h-5 rotate-90" data-id="element-18" data-mp="26lnh23bxg1e9" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-19" data-mp="3mznen7naveyc">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-20" data-mp="3n2hzh0a871sa">
            <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="pb-6 text-gray-600 text-lg leading-relaxed italic" data-id="element-21" data-mp="2tmw0zdw9fx1n">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function EsenciaAurica() {
  const screenInit = useScreenInit();
  const [viewers, setViewers] = useState(48);
  const [spots, setSpots] = useState(7);
  const [joinedCount, setJoinedCount] = useState(1010);
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  const [showSticky, setShowSticky] = useState(false);
  const [stickyInfoIndex, setStickyInfoIndex] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState('premium');
  const currentOffer = selectedOffer === 'premium' ? OFFERS.premium : OFFERS.base;
  const {
    scrollY
  } = useScroll();
  useMotionValueEvent(scrollY, 'change', latest => {
    if (latest > 600) {
      setShowSticky(true);
    } else {
      setShowSticky(false);
    }
  });
  const [recentBuyer, setRecentBuyer] = useState<{
    name: string;
    city: string;
  } | null>(null);
  const [comments, setComments] = useState(INITIAL_COMMENTS);
  const [newComment, setNewComment] = useState('');
  const [newRating, setNewRating] = useState(5);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [visibleCommentsCount, setVisibleCommentsCount] = useState(5);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    const viewerInterval = setInterval(() => {
      setViewers(prev => Math.max(10, Math.min(15, prev + (Math.random() > 0.5 ? 1 : -1))));
    }, 5000);
    const spotInterval = setInterval(() => {
      setSpots(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        const next = prev + change;
        if (next < 4) return 4;
        if (next > 8) return 8;
        return next;
      });
    }, 10000);
    const buyerInterval = setInterval(() => {
      if (Math.random() > 0.7) {
        setRecentBuyer({
          name: NAMES[Math.floor(Math.random() * NAMES.length)],
          city: ARG_CITIES[Math.floor(Math.random() * ARG_CITIES.length)]
        });
        setTimeout(() => setRecentBuyer(null), 5000);
      }
    }, 15000);
    const joinedInterval = setInterval(() => {
      setJoinedCount(prev => prev + 1);
    }, 3 * 60 * 1000);
    const stickyInterval = setInterval(() => {
      setStickyInfoIndex(prev => prev === 0 ? 1 : 0);
    }, 4000);
    return () => {
      clearInterval(timer);
      clearInterval(viewerInterval);
      clearInterval(spotInterval);
      clearInterval(buyerInterval);
      clearInterval(joinedInterval);
      clearInterval(stickyInterval);
    };
  }, []);
  const handleScrollToPurchase = () => {
    document.getElementById('purchase-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleAddComment = () => {
    if (!newComment.trim()) return;
    const comment = {
      id: Date.now(),
      name: 'Usuario',
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${Math.random()}`,
      date: 'Recién',
      rating: newRating,
      text: newComment,
      likes: 0
    };
    setComments([comment, ...comments]);
    setNewComment('');
    setShowSuccessModal(true);
  };
  const today = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="max-w-4xl mx-auto px-4 py-8 relative overflow-hidden font-sans" data-id="element-22" data-mp="1aldmzro35wfx">
      {/* Sticky Top Bar */}
      <AnimatePresence data-id="element-23" data-mp="15tn5n1qqe8f0">
        {showSticky && <motion.div initial={{
        y: -100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -100,
        opacity: 0
      }} className="fixed top-0 left-0 right-0 z-[100] px-4 py-3" data-id="element-24" data-mp="10vbxqwjzy7zf">
            <div className="max-w-4xl mx-auto bg-rose-100/60 backdrop-blur-md border border-white/40 rounded-2xl shadow-lg flex items-center justify-between px-4 md:px-8 py-2 md:py-3" data-id="element-25" data-mp="1olg0z2t9w5pw">
              <div className="flex items-center overflow-hidden h-8" data-id="element-26" data-mp="1elxh0mdd4jxu">
                <AnimatePresence mode="wait" data-id="element-27" data-mp="876b249du8wy">
                  {stickyInfoIndex === 0 ? <motion.div key="viewers" initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} exit={{
                y: -20,
                opacity: 0
              }} transition={{
                duration: 0.5
              }} className="flex items-center gap-2" data-id="element-28" data-mp="3723x4ud6qje">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-29" data-mp="32jgfxd1ukdfb" />
                      <span className="text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider" data-id="element-30" data-mp="37jb6tglmh30d">
                        {viewers} explorando
                      </span>
                    </motion.div> : <motion.div key="spots" initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} exit={{
                y: -20,
                opacity: 0
              }} transition={{
                duration: 0.5
              }} className="flex items-center gap-2" data-id="element-31" data-mp="1d7ec9ervvh13">
                      <TicketIcon className="w-3 h-3 md:w-4 md:h-4 text-[#b76e79]" data-id="element-32" data-mp="1t0gfhk0ebwxq" />
                      <span className="text-[10px] md:text-xs font-bold text-[#b76e79] uppercase tracking-wider" data-id="element-33" data-mp="1oz4u5qok1xvc">
                        {spots} cupos disponibles
                      </span>
                    </motion.div>}
                </AnimatePresence>
              </div>

              <motion.button onClick={handleScrollToPurchase} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="bg-gradient-to-r from-[#b76e79] to-[#e2b4bd] text-white px-4 md:px-6 py-2 rounded-full text-[10px] md:text-xs font-black uppercase tracking-widest shadow-md hover:shadow-rose-200/50 transition-all flex items-center gap-2" data-id="element-34" data-mp="2copgmjjx3d9d">
                Ver oferta
                <ArrowRightIcon className="w-3 h-3 md:w-4 md:h-4" data-id="element-35" data-mp="27prty6jvbrwd" />
              </motion.button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Subtle Natural Textures */}
      <div className="fixed inset-0 pointer-events-none -z-10" data-id="element-36" data-mp="1ypd5wzfiryp6">
        <div className="absolute inset-0 texture-grain opacity-30" data-id="element-37" data-mp="1ymqp096qo6ua" />
        <div className="absolute top-[10%] -left-20 w-96 h-96 bg-rose-100/40 organic-shape blur-3xl" data-id="element-38" data-mp="23opsdj2hb3r8" />
        <div className="absolute bottom-[20%] -right-20 w-80 h-80 bg-rose-200/20 organic-shape blur-3xl" data-id="element-39" data-mp="1or756l5g8ylw" />
      </div>

      {/* Header */}
      <header id="hero" className="text-center mb-12 relative z-10" data-id="element-40" data-mp="2x0o4qrdbwod6">
        {/* Hero Lavender Decorations */}
        <Lavender className="-top-10 -left-10 md:left-0" scale={1.2} rotate={-15} delay={0.2} data-id="element-41" data-mp="2s1tdr257jthz" />
        <Lavender className="-top-5 -left-4 md:left-12 opacity-60" scale={0.8} rotate={-10} delay={0.5} data-id="element-42" data-mp="2n9jntm43etfp" />
        <Lavender className="-top-12 -right-10 md:right-0" scale={1.1} rotate={15} delay={0.8} data-id="element-43" data-mp="2dcf37qpwb610" />
        <Lavender className="-top-6 -right-4 md:right-12 opacity-60" scale={0.7} rotate={10} delay={1.1} data-id="element-44" data-mp="299idlg1xu9nk" />

        {/* Trust Badges - Top Discreet Line */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-nowrap justify-center items-center gap-x-2 sm:gap-x-3 mb-8 text-[7px] sm:text-[9px] uppercase tracking-[0.12em] text-gray-500 font-medium whitespace-nowrap" data-id="element-45" data-mp="1zegpmnjefdmk">
          <div className="flex items-center gap-1" data-id="element-46" data-mp="a33a6f1qs00z">
            <GraduationCapIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37]" data-id="element-47" data-mp="a1s8ckrux2ci" />
            <span data-id="element-48" data-mp="ewy3ma75ftd9">
              <AnimatedCounter target={1000} data-id="element-49" data-mp="jty8wiw6cs3b" /> alumnos
            </span>
          </div>
          <FlowerIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37] shrink-0" data-id="element-50" data-mp="jvsk35hn95u7" />
          <div className="flex items-center gap-1" data-id="element-51" data-mp="3idep3h0ggrgn">
            <ZapIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37]" data-id="element-52" data-mp="3n9va42ccyppi" />
            <span data-id="element-53" data-mp="3s9rhmbk9kz9t">Acceso inmediato</span>
          </div>
          <FlowerIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37] shrink-0" data-id="element-54" data-mp="3s86lihiax89u" />
          <div className="flex items-center gap-1" data-id="element-55" data-mp="148556afovz9">
            <ShieldCheckIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37]" data-id="element-56" data-mp="17jitf67z7xep" />
            <span data-id="element-57" data-mp="17lv89aiftrnj">Garantía 7 días</span>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className="mb-8 relative pt-[0px] pb-[0px]" data-id="element-58" data-mp="1ch29mbo8ik9a">
          {/* Top ornament */}

          {/* "Bienvenida a" intro */}
          <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="italic text-gray-500 md:text-2xl tracking-[0.2em] font-light uppercase text-[14px] pt-[0px] pb-[0px] mt-[0px] mb-[0px]" data-id="element-59" data-mp="3dlmizai86qxh">
            BIENVENIDA/O A ESENCIA ÁURICA
          </p>

          {/* Main Title — bohemian premium */}
          <div className="relative inline-block mb-4 overflow-hidden group" data-id="element-60" data-mp="2twlshhvpa940">
            <div className="absolute inset-0 -inset-x-12 -inset-y-4 bg-gradient-radial from-rose-100/60 via-rose-50/20 to-transparent blur-3xl -z-10" aria-hidden="true" data-id="element-61" data-mp="2671bv5n0rnxw" />
            <h1 style={{
            fontFamily: "'Playfair Display', serif",
            letterSpacing: '0.06em',
            textShadow: '0 2px 4px rgba(0,0,0,0.1), 0 4px 20px rgba(184,134,92,0.25)'
          }} className="sm:text-3xl md:text-4xl font-bold text-[#5c4033] relative whitespace-nowrap pt-[0px] pb-[0px] pl-[0px] pr-[0px] mt-[0px] mb-[0px] ml-[0px] mr-[0px] text-[20px]" data-id="element-62" data-mp="1s17si8tx6fqc">
              VOLVÉ A CONECTAR{' '}
              <span className="italic gold-deep-text" data-id="element-63" data-mp="2frv87ga2u9ng">con vos</span>
              {/* Animated Reflection/Shine */}
              <motion.div className="absolute inset-0 w-full h-full" style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 70%, transparent 100%)',
              transform: 'skewX(-20deg)'
            }} animate={{
              x: ['-150%', '250%']
            }} transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3,
              ease: 'easeInOut'
            }} data-id="element-64" data-mp="20t1bs30u24jm" />
            </h1>
          </div>

          {/* Bottom botanical ornament */}
          <div className="flex items-center justify-center gap-3 pt-[0px] pb-[0px] mt-[0px] mb-[0px]" data-id="element-65" data-mp="3tuvxf0zw69pv">
            <div className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent via-[#d4af37]/50 to-[#d4af37]/80" data-id="element-66" data-mp="ud67hwvekl7h" />
            <svg className="w-10 h-4 text-[#d4af37]" viewBox="0 0 40 12" fill="none" data-id="element-67" data-mp="ph7bpo7qw0x1">
              <path d="M2 6 Q 10 0, 20 6 T 38 6" stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.7" data-id="element-68" data-mp="gncc0vpe72os" />
              <circle cx="20" cy="6" r="1.5" fill="currentColor" data-id="element-69" data-mp="18cxcj83xyr9c" />
              <circle cx="6" cy="6" r="0.8" fill="currentColor" opacity="0.6" data-id="element-70" data-mp="2r0qkg5ctpcpq" />
              <circle cx="34" cy="6" r="0.8" fill="currentColor" opacity="0.6" data-id="element-71" data-mp="2vzjwrugdixob" />
            </svg>
            <div className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent via-[#d4af37]/50 to-[#d4af37]/80" data-id="element-72" data-mp="303hx28hfqeiy" />
          </div>

          {/* Subtitle */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} className="relative max-w-2xl mx-auto mt-6" data-id="element-73" data-mp="39yjk8ys5h6iw">
            <div className="absolute inset-0 bg-white/40 backdrop-blur-sm rounded-2xl -z-10 border border-rose-100/50 shadow-sm" data-id="element-74" data-mp="3sq2n95m6ckkx" />
            <p style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} className="md:text-lg lg:text-xl text-gray-800 px-6 py-4 font-medium tracking-wide uppercase text-[14px] pt-[0px] pb-[0px] pl-[16px] pr-[16px] mt-[0px] mb-[0px]" data-id="element-75" data-mp="1kkrs74e03a5">
              Creá perfumes naturales, brumas áuricas y Agua Florida desde cero.
              Descubrí una nueva habilidad para disfrutar, regalar o convertir en
              un proyecto propio.
            </p>
          </motion.div>
        </motion.div>
        <div className="mb-6 relative" onClick={handleScrollToPurchase} data-id="element-76" data-mp="dlz0k18nql31">
          {/* Golden Orbs - subtle ambient particles behind the book */}
          <div className="absolute inset-0 pointer-events-none overflow-visible" data-id="element-77" data-mp="ie07r8qyxlm7">
            {[{
            size: 16,
            top: '12%',
            left: '18%',
            delay: 0,
            duration: 5
          }, {
            size: 8,
            top: '25%',
            left: '8%',
            delay: 1.2,
            duration: 6
          }, {
            size: 12,
            top: '60%',
            left: '12%',
            delay: 0.6,
            duration: 4.5
          }, {
            size: 6,
            top: '78%',
            left: '22%',
            delay: 2,
            duration: 5.5
          }, {
            size: 20,
            top: '8%',
            right: '14%',
            delay: 0.3,
            duration: 5
          }, {
            size: 10,
            top: '40%',
            right: '6%',
            delay: 1.5,
            duration: 6
          }, {
            size: 14,
            top: '68%',
            right: '10%',
            delay: 0.9,
            duration: 4.8
          }, {
            size: 7,
            top: '85%',
            right: '20%',
            delay: 2.2,
            duration: 5.2
          }, {
            size: 5,
            top: '50%',
            left: '50%',
            delay: 1.8,
            duration: 4
          }, {
            size: 9,
            top: '20%',
            left: '45%',
            delay: 2.5,
            duration: 5.5
          }].map((orb, i) => <motion.div key={i} className="absolute rounded-full" style={{
            width: orb.size,
            height: orb.size,
            top: orb.top,
            left: orb.left,
            right: orb.right,
            background: 'radial-gradient(circle, rgba(252,246,186,0.95) 0%, rgba(212,175,55,0.6) 40%, rgba(191,149,63,0.2) 70%, transparent 100%)',
            boxShadow: '0 0 12px rgba(212,175,55,0.6), 0 0 24px rgba(212,175,55,0.3)'
          }} animate={{
            y: [0, -20, 0],
            opacity: [0.3, 0.9, 0.3],
            scale: [0.8, 1.1, 0.8]
          }} transition={{
            duration: orb.duration,
            delay: orb.delay,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-78" data-mp="xvkzbatzejjr" data-mpi={i} />)}
          </div>

          {/* Floating Book */}
          <motion.div className="cursor-pointer relative z-10" animate={{
          y: [0, -12, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-79" data-mp="lcp8sx2j0kgm">
            <img src="https://cdn.magicpatterns.com/uploads/7Y4431CDVYTrMq5JAmfkxX/Libro_principal_PNG_MOCKUP.webp" alt="Esencia Áurica" className="w-full max-w-sm mx-auto drop-shadow-2xl" data-id="element-80" data-mp="322nrequxoxxe" />
          </motion.div>

          {/* Deluxe Hero Text */}
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8,
          duration: 1
        }} style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: '#b8865c'
        }} className="mt-8 text-sm md:text-lg italic font-medium tracking-[0.1em] uppercase" data-id="element-81" data-mp="1t3mnj04e0n8a">
            TU PRIMER PERFUME NATURAL PUEDE NACER ESTA SEMANA
          </motion.p>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="relative mt-4 mb-2" data-id="element-82" data-mp="1j8b6uc9f55xw">
          {/* Rose gold gradient frame */}
          <div className="relative p-[1.5px] rounded-2xl" style={{
          background: 'linear-gradient(135deg, #d4a574 0%, #e8b89a 25%, #f5d5c0 50%, #e8b89a 75%, #b8865c 100%)',
          boxShadow: '0 8px 32px rgba(184,134,92,0.15), 0 2px 8px rgba(212,165,116,0.2)'
        }} data-id="element-83" data-mp="e60hyod8mgs7">
            <div className="relative rounded-2xl px-6 py-7 md:px-10 md:py-9 overflow-hidden pt-[13px] pb-[13px] mt-[9px] mb-[9px]" style={{
            background: 'linear-gradient(135deg, #fdf6ee 0%, #faeee0 50%, #f5e3d0 100%)'
          }} data-id="element-84" data-mp="3rly6e4uevenv">
              {/* Subtle corner ornaments */}
              <div className="absolute top-3 left-3 w-6 h-6 border-t border-l border-[#b8865c]/40 rounded-tl-lg" data-id="element-85" data-mp="38o5dzwheyn4q" />
              <div className="absolute top-3 right-3 w-6 h-6 border-t border-r border-[#b8865c]/40 rounded-tr-lg" data-id="element-86" data-mp="2z000tw5o3nr8" />
              <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-[#b8865c]/40 rounded-bl-lg" data-id="element-87" data-mp="34047yvkor64s" />
              <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-[#b8865c]/40 rounded-br-lg" data-id="element-88" data-mp="2p3n0wgjj4do1" />

              <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: '#3d2b1f'
            }} className="text-lg md:text-2xl italic leading-relaxed tracking-wide pt-[0px] pb-[0px]" data-id="element-89" data-mp="2u2zvjngn6p16">
                No necesitás experiencia. Solo{' '}
                <span className="font-semibold not-italic gold-deep-text" data-id="element-90" data-mp="3cziwvql7f8n6">
                  ganas de empezar
                </span>
                .
              </p>
              <div className="flex items-center justify-center gap-2 my-4 pt-[0px] pb-[0px] mt-[0px] mb-[0px]" data-id="element-91" data-mp="4ieczd1carfj">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#b8865c]/60" data-id="element-92" data-mp="1u1cvcel7w4mj" />
                <div className="w-1 h-1 rounded-full bg-[#b8865c]" data-id="element-93" data-mp="1p29qau58lyek" />
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#b8865c]/60" data-id="element-94" data-mp="1p1zyr24jnj6r" />
              </div>
              <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: '#3d2b1f'
            }} className="text-base md:text-xl leading-relaxed tracking-wide font-light" data-id="element-95" data-mp="23t8ttqr7twra">
                Miles de personas descubrieron que podían crear cuando dejaron
                de esperar el .
              </p>
            </div>
          </div>
        </motion.div>
      </header>

      {/* Sales Copy Sections */}
      <section id="oferta" className="mb-16 relative z-10 px-2 space-y-[51px]" data-id="element-96" data-mp="1g3obna2vz3j2">
        {/* The Dream */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-97" data-mp="2vkexgh65aclb">
          <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="text-2xl md:text-3xl text-gray-700 italic leading-relaxed" data-id="element-98" data-mp="tsd3y17ykjw3">
            Imaginás tu propia{' '}
            <span className="gold-deep-text font-bold not-italic" data-id="element-99" data-mp="13kaklnhqk427">marca</span>.{' '}
            <br className="hidden md:block" data-id="element-100" data-mp="17k1gu6q17ce4" />
            Tus propias{' '}
            <span className="gold-deep-text font-bold not-italic" data-id="element-101" data-mp="1ciutjwxhuzd8">
              creaciones
            </span>
            . <br className="hidden md:block" data-id="element-102" data-mp="1hk25m86o6kpp" />
            Tus propios{' '}
            <span className="gold-deep-text font-bold not-italic" data-id="element-103" data-mp="1midbk7tugb2n">aromas</span>.
          </p>
          <div className="flex justify-center mt-6" data-id="element-104" data-mp="3janana80c8po">
            <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#d4af37]/40 to-transparent" data-id="element-105" data-mp="3jbydn7tmpcfq" />
          </div>
        </motion.div>

        {/* The Reality Check */}

        {/* The Solution / Manifest */}
        <div className="relative" data-id="element-106" data-mp="25fx59alvs71m">
          <Lavender className="top-1/2 -left-12 opacity-40 hidden md:block" scale={0.9} rotate={-20} data-id="element-107" data-mp="20n3x0cr7egks" />
          <Lavender className="top-1/3 -right-12 opacity-40 hidden md:block" scale={0.8} rotate={20} data-id="element-108" data-mp="1wjo3vqeceycu" />

          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="relative p-8 md:p-14 rounded-[3rem] bg-gradient-to-b from-white/60 to-rose-50/40 border border-rose-200/60 shadow-xl text-center" data-id="element-109" data-mp="2od82yqrv6sey">
            {/* Decorative Rose Gold Elements */}
            <div className="absolute -top-4 -left-4 w-12 h-12 text-[#e2b4bd] opacity-40" data-id="element-110" data-mp="yxh02ipv5a1j">
              <FlowerIcon className="w-full h-full" data-id="element-111" data-mp="3tbi76g2rniqv" />
            </div>
            <div className="absolute -bottom-4 -right-4 w-12 h-12 text-[#e2b4bd] opacity-40 rotate-180" data-id="element-112" data-mp="3t9nm91rhvqms">
              <FlowerIcon className="w-full h-full" data-id="element-113" data-mp="3oau58zd01hhv" />
            </div>

            <h3 style={{
            fontFamily: "'Playfair Display', serif"
          }} className="text-2xl md:text-3xl font-bold mb-8 gold-deep-text" data-id="element-114" data-mp="608pzddcnd4g">
              Esencia Áurica
            </h3>

            <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-light italic mb-10" data-id="element-115" data-mp="ay0sf1bmehmw">
              No es solo una guía para crear perfumes. <br data-id="element-116" data-mp="ox06xg5f5g5c" />
              Es una invitación a volver a dedicarte tiempo. <br data-id="element-117" data-mp="kvewe1gcxdyb" />
              A aprender algo que te entusiasme. <br data-id="element-118" data-mp="ktu30f5s4121" />A descubrir una actividad
              que te conecte con tu esencia, tu creatividad y tu deseo de
              construir algo propio.
            </p>

            <motion.a href={currentOffer.link} className="relative overflow-hidden px-6 py-3 rounded-full mx-auto flex items-center gap-2 text-sm md:text-base font-semibold tracking-wide shadow-lg cursor-pointer group w-fit" style={{
            background: 'linear-gradient(135deg, #d4a574 0%, #e8b89a 30%, #f5d5c0 50%, #e8b89a 70%, #b8865c 100%)',
            color: '#5a3a1a',
            border: '1px solid rgba(212, 165, 116, 0.4)'
          }} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} data-id="element-119" data-mp="2momlu3popbxx">
              {/* Animated shine effect */}
              <motion.div className="absolute inset-0 w-full h-full" style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
              transform: 'translateX(-100%)'
            }} animate={{
              transform: ['translateX(-100%)', 'translateX(200%)']
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              repeatDelay: 2
            }} data-id="element-120" data-mp="1jx20735afcha" />
              <span className="relative z-10 whitespace-nowrap" data-id="element-121" data-mp="32tx12o74lov1">
                Empezar mi camino creativo
              </span>
              <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5 relative z-10 group-hover:translate-x-1 transition-transform" data-id="element-122" data-mp="4agcb1os4h63" />
            </motion.a>
          </motion.div>
        </div>

        {/* "Esto es para vos si..." Section */}
        <div className="mt-24 mb-20 relative" data-id="element-123" data-mp="3qozocrqa7k9e">
          <div className="text-center px-4 mt-[0px] mb-[0px] pt-[0px] pb-[0px]" data-id="element-124" data-mp="3lr7x7zxgo269">
            <div className="relative inline-block mb-6" data-id="element-125" data-mp="n2r9qg0nzmwt">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} style={{
              fontFamily: "'Playfair Display', serif",
              letterSpacing: '0.02em',
              textShadow: '0 2px 10px rgba(184,134,92,0.1)'
            }} className="md:text-5xl font-bold text-[#3d2b1f] relative overflow-hidden px-2 py-1 text-[24px]" data-id="element-126" data-mp="n4c3i3f5n1tm">
                No necesitás otro perfume.
                {/* Subtle Golden Shine */}
                <motion.div className="absolute inset-0 w-full h-full pointer-events-none" style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(212,175,55,0) 30%, rgba(212,175,55,0.2) 50%, rgba(212,175,55,0) 70%, transparent 100%)',
                transform: 'skewX(-20deg)'
              }} animate={{
                x: ['-150%', '250%']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 4,
                ease: 'easeInOut'
              }} data-id="element-127" data-mp="1iat4jbs62b28" />
              </motion.h2>
              <motion.div initial={{
              scale: 0,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5,
              duration: 0.8
            }} className="mt-2 flex justify-center items-center gap-3" data-id="element-128" data-mp="2yrrzpvdq27ri">
                <div className="h-px w-8 bg-[#b8865c]/30" data-id="element-129" data-mp="3vfepxbk7ctwi" />
                <FlowerIcon className="w-4 h-4 text-[#b8865c]/60" data-id="element-130" data-mp="3qiej2yfj3n4c" />
                <div className="h-px w-8 bg-[#b8865c]/30" data-id="element-131" data-mp="3qk92u9f2d96u" />
              </motion.div>
            </div>
            <motion.p initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} className="md:text-2xl italic text-gray-600 text-[20px]" data-id="element-132" data-mp="2vvni6j8xvz0">
              Necesitás volver a sentir la emoción de crear algo tuyo.
            </motion.p>
          </div>

          <div className="max-w-3xl mx-auto pt-[0px] pb-[0px] space-y-[41px]" data-id="element-133" data-mp="gtaj5t03ezzf">
            {/* Point 01 */}

            {/* Point 02 */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="flex flex-col md:flex-row-reverse items-center gap-8 md:gap-12" data-id="element-134" data-mp="14htrg8j57cri">
              <div className="w-full md:w-1/2 relative" data-id="element-135" data-mp="196mijqbud93h">
                <div className="absolute -inset-4 bg-rose-200/20 rounded-full blur-2xl -z-10" data-id="element-136" data-mp="1998ndi7clai0" />
              </div>
            </motion.div>

            {/* Point 03 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="relative p-[1.5px] rounded-[2rem] overflow-hidden" style={{
            background: 'linear-gradient(135deg, #d4a574 0%, #f5d5c0 50%, #b8865c 100%)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3), 0 0 20px rgba(184,134,92,0.2)'
          }} data-id="element-137" data-mp="108aikxp34yyd">
              <div className="relative p-8 md:p-12 rounded-[2rem] bg-[#2d3a26] text-white overflow-hidden pt-[13px] pb-[13px]" data-id="element-138" data-mp="2o6tbjm2jlsxs">
                {/* Animated Shine Sweep */}
                <motion.div className="absolute inset-0 w-full h-full z-20 pointer-events-none" style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0) 60%, transparent 100%)',
                transform: 'skewX(-20deg)'
              }} animate={{
                x: ['-150%', '250%']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 4,
                ease: 'easeInOut'
              }} data-id="element-139" data-mp="2x5yb8vpubsbj" />

                <div className="absolute top-0 right-0 w-64 h-64 bg-[#5a7a4a]/20 blur-3xl -z-10" data-id="element-140" data-mp="1qn01mjx0basg" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#b8865c]/10 blur-3xl -z-10" data-id="element-141" data-mp="k3v1aadjwejw" />

                {/* Golden Corner Ornaments */}
                <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-[#d4af37]/40 rounded-tl-xl" data-id="element-142" data-mp="t2p2ke1ttt3s" />
                <div className="absolute top-4 right-4 w-8 h-8 border-t border-r border-[#d4af37]/40 rounded-tr-xl" data-id="element-143" data-mp="p029yaxc045j" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b border-l border-[#d4af37]/40 rounded-bl-xl" data-id="element-144" data-mp="12ys6npg8o1td" />
                <div className="absolute bottom-4 right-4 w-8 h-8 border-b border-r border-[#d4af37]/40 rounded-br-xl" data-id="element-145" data-mp="xzp9ssaex1wh" />

                <div className="text-center relative z-10" data-id="element-146" data-mp="17rlc6fjj7e6i">
                  <h4 style={{
                  fontFamily: "'Playfair Display', serif"
                }} className="text-2xl md:text-4xl font-bold mb-6 uppercase tracking-widest text-[#f5d5c0] drop-shadow-md pt-[5px] pb-[5px]" data-id="element-147" data-mp="17s4ptk25fgeq">
                    LOS AROMAS PUEDEN SER EL COMIENZO
                  </h4>
                  <p style={{
                  fontFamily: "'Cormorant Garamond', serif"
                }} className="text-lg md:text-2xl leading-relaxed italic font-light" data-id="element-148" data-mp="25bl8lvby08p6">
                    Crear tus propios perfumes naturales y brumas áuricas te
                    conecta con la calma, la creatividad y el bienestar.
                    <br data-id="element-149" data-mp="1metrnsjvi5oa" />
                    <br data-id="element-150" data-mp="1mfd10kvgjlqp" />
                    <span className="text-[#f5d5c0] font-medium not-italic uppercase tracking-tighter text-sm md:text-base border-b border-[#d4af37]/30 pb-1" data-id="element-151" data-mp="17p5r4tjinb1m">
                      Porque no se trata solamente de elaborar aromas.
                    </span>
                    <br data-id="element-152" data-mp="yozdn7ntib2g" />
                    <span className="text-2xl md:text-3xl block mt-6 font-semibold gold-deep-text" data-id="element-153" data-mp="yqtotu9aeotc">
                      Se trata de volver a conectar con vos.
                    </span>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* "Esto es para vos si..." Section */}
        <div className="mt-20 mb-16" data-id="element-154" data-mp="1kxwufi14pu9d">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-155" data-mp="1kwvilbcah3ql">
            <h2 style={{
            fontFamily: "'Playfair Display', serif"
          }} className="md:text-4xl font-bold text-gray-800 mb-4 text-[20px]" data-id="element-156" data-mp="1fxims13fwmbv">
              Quizás te veas reflejada en esto...
            </h2>
            <div className="h-px w-20 bg-[#e2b4bd] mx-auto" data-id="element-157" data-mp="2wewiu2kcvoy4" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-id="element-158" data-mp="31bwnqa5gylnn">
            {[{
            text: 'Buscás un espacio de calma y conexión a través de la creación manual.',
            icon: HeartIcon
          }, {
            text: 'Querés aprender a formular tus propios perfumes y brumas con ingredientes 100% naturales.',
            icon: FlowerIcon
          }, {
            text: 'Soñás con tener un emprendimiento con propósito o simplemente regalar algo hecho por vos.',
            icon: ZapIcon
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: i % 2 === 0 ? -20 : 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group p-6 rounded-2xl bg-white/40 border border-rose-100/50 hover:border-[#e8b89a]/50 transition-all duration-300 shadow-sm flex items-start gap-4" data-id="element-159" data-mp="72of5izf3fux" data-mpi={i}>
                <div className="p-2 rounded-lg bg-gradient-to-br from-rose-50 to-white border border-rose-100 group-hover:scale-110 transition-transform duration-300" data-id="element-160" data-mp="hp81azsu3bxl">
                  <item.icon className="w-5 h-5 text-[#b8865c]" data-id="element-161" data-mp="qr7ipt7rgi12" />
                </div>
                <p className="text-gray-700 leading-relaxed font-light md:text-lg" data-id="element-162" data-mp="vq103wq64t6i">
                  {item.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* "Lo que vas a aprender" Section */}
        <div className="mt-24 mb-20 relative" data-id="element-163" data-mp="15eix02vbug4v">
          <div className="absolute inset-0 bg-[#fdf2f2]/30 blur-3xl -z-10 rounded-full" data-id="element-164" data-mp="1aex6lpckty50" />

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-14" data-id="element-165" data-mp="1ad3x82akwztb">
            <h2 style={{
            fontFamily: "'Playfair Display', serif"
          }} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-166" data-mp="2pxmnaxjp0xp8">
              Lo que vas a{' '}
              <span className="gold-deep-text italic" data-id="element-167" data-mp="3iliks4h36tn2">aprender</span>
            </h2>
            <p style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} className="text-lg md:text-xl text-gray-600 italic" data-id="element-168" data-mp="3dn79dpb58b2q">
              Un recorrido profundo desde la esencia hasta la creación real.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto px-4" data-id="element-169" data-mp="58p4txkevdnv">
            {[{
            title: 'Perfumería Natural',
            text: 'Cómo crear tus propios perfumes naturales desde cero, entendiendo las notas y fijación.',
            icon: ZapIcon
          }, {
            title: 'Brumas Áuricas',
            text: 'El arte de las brumas áuricas para limpiar, proteger y armonizar tus espacios y energía.',
            icon: FlowerIcon
          }, {
            title: 'Aromaterapia Consciente',
            text: 'Secretos de la aromaterapia y el uso sagrado de aceites esenciales para el bienestar.',
            icon: ShieldCheckIcon
          }, {
            title: 'Tu Propio Proyecto',
            text: 'Cómo transformar tu pasión en un emprendimiento auténtico, rentable y con propósito.',
            icon: GraduationCapIcon
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="relative p-8 rounded-[2rem] bg-white/60 border border-rose-100/40 shadow-sm hover:shadow-md transition-shadow duration-500 group" data-id="element-170" data-mp="mmgdbaqod3k2" data-mpi={i}>
                <div className="absolute top-6 right-8 text-[#d4af37]/10 group-hover:text-[#d4af37]/20 transition-colors duration-500" data-id="element-171" data-mp="1t8n5diwlf1di">
                  <item.icon className="w-12 h-12" data-id="element-172" data-mp="227qogmvrfgs0" />
                </div>

                <h4 style={{
              fontFamily: "'Playfair Display', serif"
            }} className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2" data-id="element-173" data-mp="224ut96bar679">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#b8865c]" data-id="element-174" data-mp="38o2pcvpyvcra" />
                  {item.title}
                </h4>
                <p style={{
              fontFamily: "'Cormorant Garamond', serif"
            }} className="text-gray-600 text-lg leading-relaxed" data-id="element-175" data-mp="269d9c9shg0o9">
                  {item.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* "Qué incluye la guía" Section */}
        <div className="mt-24 mb-20" data-id="element-176" data-mp="1nkf1680ca2ah">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative p-[1.5px] rounded-[3rem]" style={{
          background: 'linear-gradient(135deg, #d4a574 0%, #e8b89a 25%, #f5d5c0 50%, #d4af37 75%, #b8865c 100%)',
          boxShadow: '0 20px 60px rgba(184,134,92,0.15), 0 8px 24px rgba(212,175,55,0.1)'
        }} data-id="element-177" data-mp="1nhasaxbxiidh">
            <div className="bg-white/60 backdrop-blur-sm rounded-[3rem] p-8 md:p-16 relative overflow-hidden" data-id="element-178" data-mp="wbvoy1d6rd4q">
              {/* Decorative background element */}
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#fdf2f2] rounded-full blur-3xl opacity-60" data-id="element-179" data-mp="rb7okp32hjxu" />
              <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fef5e7] rounded-full blur-3xl opacity-50" data-id="element-180" data-mp="nd9ylk1h2tte" />

              {/* Ornamental gold corners */}
              <div className="absolute top-6 left-6 w-10 h-10 border-t-2 border-l-2 border-[#d4af37]/50 rounded-tl-3xl pointer-events-none" data-id="element-181" data-mp="dkcjzozlpp02" />
              <div className="absolute top-6 right-6 w-10 h-10 border-t-2 border-r-2 border-[#d4af37]/50 rounded-tr-3xl pointer-events-none" data-id="element-182" data-mp="8jydibdnen45" />
              <div className="absolute bottom-6 left-6 w-10 h-10 border-b-2 border-l-2 border-[#d4af37]/50 rounded-bl-3xl pointer-events-none" data-id="element-183" data-mp="3vra82iha3j1x" />
              <div className="absolute bottom-6 right-6 w-10 h-10 border-b-2 border-r-2 border-[#d4af37]/50 rounded-br-3xl pointer-events-none" data-id="element-184" data-mp="3qu0kz9hd3jh9" />

              {/* Subtle animated floating flowers - appear occasionally */}
              <motion.div className="absolute top-[15%] right-[8%] text-[#d4af37] pointer-events-none" initial={{
              opacity: 0,
              scale: 0.5,
              rotate: -20
            }} animate={{
              opacity: [0, 0.35, 0.35, 0],
              scale: [0.5, 1, 1, 0.5],
              y: [0, -15, -15, -30],
              rotate: [-20, 10, 10, 30]
            }} transition={{
              duration: 7,
              times: [0, 0.2, 0.8, 1],
              repeat: Infinity,
              repeatDelay: 5,
              ease: 'easeInOut'
            }} data-id="element-185" data-mp="19ijgm9ntrsm6">
                <FlowerIcon className="w-6 h-6" strokeWidth={1.2} data-id="element-186" data-mp="lt8bkfzfzc28" />
              </motion.div>
              <motion.div className="absolute bottom-[25%] left-[6%] text-[#b8865c] pointer-events-none" initial={{
              opacity: 0,
              scale: 0.5,
              rotate: 15
            }} animate={{
              opacity: [0, 0.3, 0.3, 0],
              scale: [0.5, 0.9, 0.9, 0.5],
              y: [0, -20, -20, -40],
              rotate: [15, -10, -10, -25]
            }} transition={{
              duration: 8,
              times: [0, 0.2, 0.8, 1],
              repeat: Infinity,
              repeatDelay: 6,
              delay: 2.5,
              ease: 'easeInOut'
            }} data-id="element-187" data-mp="26naeljytjjg">
                <FlowerIcon className="w-5 h-5" strokeWidth={1.2} data-id="element-188" data-mp="pyua9uowg8nm" />
              </motion.div>
              <motion.div className="absolute top-[45%] right-[14%] text-[#e2b4bd] pointer-events-none" initial={{
              opacity: 0,
              scale: 0.5
            }} animate={{
              opacity: [0, 0.28, 0.28, 0],
              scale: [0.5, 1.1, 1.1, 0.5],
              y: [0, -12, -12, -25],
              rotate: [0, 25, 25, 45]
            }} transition={{
              duration: 6.5,
              times: [0, 0.2, 0.8, 1],
              repeat: Infinity,
              repeatDelay: 7,
              delay: 5,
              ease: 'easeInOut'
            }} data-id="element-189" data-mp="13ufyw0r80qfi">
                <FlowerIcon className="w-4 h-4" strokeWidth={1.2} data-id="element-190" data-mp="18kowxa0mum8t" />
              </motion.div>

              <div className="relative z-10 max-w-3xl mx-auto" data-id="element-191" data-mp="13lvk7jt66z9p">
                <div className="text-center mb-12" data-id="element-192" data-mp="13noqgxzvfhh6">
                  {/* Top mini-ornament */}
                  <div className="flex items-center justify-center gap-2 mb-4 opacity-70" data-id="element-193" data-mp="ylzd3bp6erpx">
                    <div className="h-px w-8 bg-gradient-to-r from-transparent to-[#d4af37]/60" data-id="element-194" data-mp="ulp6bwtk7xa0" />
                    <svg className="w-3 h-3 text-[#d4af37]" viewBox="0 0 12 12" fill="currentColor" data-id="element-195" data-mp="367zfhzvohofz">
                      <path d="M6 0 L7 5 L12 6 L7 7 L6 12 L5 7 L0 6 L5 5 Z" data-id="element-196" data-mp="3faihkhrn3x16" />
                    </svg>
                    <div className="h-px w-8 bg-gradient-to-l from-transparent to-[#d4af37]/60" data-id="element-197" data-mp="3k6z79gehmjgd" />
                  </div>
                  <h2 style={{
                  fontFamily: "'Playfair Display', serif",
                  textShadow: '0 2px 12px rgba(184,134,92,0.15)'
                }} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-198" data-mp="2mmcs5hpn5jv0">
                    Qué incluye la{' '}
                    <span className="gold-deep-text italic" data-id="element-199" data-mp="23uttto6katzb">guía</span>
                  </h2>
                  {/* Elaborate gold divider */}
                  <div className="flex items-center justify-center gap-3 mt-4" data-id="element-200" data-mp="28raicjhobacx">
                    <div className="h-px w-12 bg-gradient-to-r from-transparent via-[#d4af37]/40 to-[#d4af37]/80" data-id="element-201" data-mp="20r4ghrqx38vl" />
                    <svg className="w-4 h-4 text-[#d4af37]" viewBox="0 0 16 16" fill="currentColor" data-id="element-202" data-mp="1wqlnhudafwrt">
                      <circle cx="8" cy="8" r="2" data-id="element-203" data-mp="25jp9ir201hy2" />
                      <path d="M8 0 L8.5 6 L8 8 L7.5 6 Z M8 16 L8.5 10 L8 8 L7.5 10 Z M0 8 L6 7.5 L8 8 L6 8.5 Z M16 8 L10 7.5 L8 8 L10 8.5 Z" opacity="0.8" data-id="element-204" data-mp="1htbcsn8gyux5" />
                    </svg>
                    <div className="h-px w-12 bg-gradient-to-l from-transparent via-[#d4af37]/40 to-[#d4af37]/80" data-id="element-205" data-mp="1rtbxodlzlj3g" />
                  </div>
                </div>

                <div className="space-y-6" data-id="element-206" data-mp="3364qlrqikdl3">
                  {[{
                  title: 'El despertar de los sentidos',
                  text: 'Introducción profunda al mundo de la perfumería natural.'
                }, {
                  title: 'Tu laboratorio sagrado',
                  text: 'Herramientas y materiales básicos para comenzar tu camino.'
                }, {
                  title: 'Alquimia de aromas',
                  text: 'Clasificación de aceites esenciales y el arte de las notas olfativas.'
                }, {
                  title: 'Recetario exclusivo',
                  text: 'Fórmulas probadas para crear tus propios perfumes y brumas.'
                }, {
                  title: 'Energía en movimiento',
                  text: 'Rituales de intención y técnicas de limpieza energética.'
                }, {
                  title: 'Tu marca auténtica',
                  text: 'Guía paso a paso para transformar tu pasión en un proyecto real.'
                }, {
                  title: 'Tu kit creativo',
                  text: 'Recursos descargables y etiquetas editables listas para usar.'
                }].map((item, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  x: -10
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.1
                }} className="flex items-start gap-5 group" data-id="element-207" data-mp="3pwsdqb00hly5" data-mpi={i}>
                      <div className="mt-1 flex-shrink-0" data-id="element-208" data-mp="157m4qgbooy34">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm" style={{
                      background: 'linear-gradient(135deg, #fdf6ee 0%, #f5e3d0 50%, #e8b89a 100%)',
                      border: '1px solid rgba(212, 175, 55, 0.5)',
                      boxShadow: '0 2px 8px rgba(184, 134, 92, 0.15)'
                    }} data-id="element-209" data-mp="10fb4tdgy1cak">
                          <svg className="w-3 h-3 text-[#b8865c]" viewBox="0 0 12 12" fill="currentColor" data-id="element-210" data-mp="1qnqz90dpueol">
                            <path d="M6 0 L7 5 L12 6 L7 7 L6 12 L5 7 L0 6 L5 5 Z" data-id="element-211" data-mp="25e7zigem6yjw" />
                          </svg>
                        </div>
                      </div>
                      <div data-id="element-212" data-mp="29e8fw6l9xjtl">
                        <h4 style={{
                      fontFamily: "'Playfair Display', serif"
                    }} className="text-lg md:text-xl font-bold text-gray-800 leading-tight group-hover:text-[#b8865c] transition-colors duration-300" data-id="element-213" data-mp="2eempvu6fr3u9">
                          {item.title}
                        </h4>
                        <p style={{
                      fontFamily: "'Cormorant Garamond', serif"
                    }} className="text-gray-600 text-lg italic font-light" data-id="element-214" data-mp="jbhqn1m2p73r">
                          {item.text}
                        </p>
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* "Sobre mí" Section */}

        {/* Quick Benefits Grid - Reduced size by 40% */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-8 max-w-2xl mx-auto" data-id="element-215" data-mp="33udvfomrqcxm">
          {[{
          title: 'Aceites esenciales',
          icon: '🌿'
        }, {
          title: 'Aromas naturales',
          icon: '🌸'
        }, {
          title: 'Energía e intención',
          icon: '✨'
        }, {
          title: 'Elementos holísticos',
          icon: '💎'
        }].map((item, i) => <motion.div key={i} whileHover={{
          y: -3
        }} className="flex flex-col items-center gap-1.5 p-3.5 bg-white/40 backdrop-blur-xs rounded-2xl border border-rose-50/50 shadow-sm text-center" data-id="element-216" data-mp="10kka1rf2pcuy" data-mpi={i}>
              <span className="text-xl" data-id="element-217" data-mp="vnaavk1vtmtx">{item.icon}</span>
              <span className="text-[10px] md:text-[11px] font-semibold text-gray-500 uppercase tracking-widest leading-tight" data-id="element-218" data-mp="2lyoo4lrctl59">
                {item.title}
              </span>
            </motion.div>)}
        </div>
      </section>

      {/* Bonuses Section */}
      <section id="bonos" className="mb-16 relative z-10" data-id="element-219" data-mp="1dq4zzou50pbd">
        <Lavender className="top-0 -right-10 opacity-30" scale={1.5} rotate={15} data-id="element-220" data-mp="18rk0jrmpm9nz" />
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16 relative" data-id="element-221" data-mp="13zsg7yu5d06a">
          {/* Subtle background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-32 bg-rose-100/30 blur-3xl -z-10" data-id="element-222" data-mp="q3os8n9v9o29" />

          <span className="inline-block px-4 py-1.5 rounded-full bg-rose-50 border border-rose-100 text-[#b76e79] text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-6" data-id="element-223" data-mp="g3o3umx54hr7">
            Regalo exclusivo
          </span>

          <h2 style={{
          fontFamily: "'Playfair Display', serif"
        }} className="text-3xl md:text-5xl font-bold mb-6 text-gray-800 leading-tight" data-id="element-224" data-mp="2ahp3ha0il8sc">
            Expandí tu universo con <br data-id="element-225" data-mp="2mxf0zxg7zluh" />
            <span className="gold-deep-text italic" data-id="element-226" data-mp="2rqrog495xmln">6 Ebooks de regalo</span>
          </h2>

          <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="text-lg md:text-2xl text-gray-600 max-w-2xl mx-auto leading-relaxed italic font-light" data-id="element-227" data-mp="2wpl2bxsd3fmc">
            Diseñados para acompañarte en cada paso y llevar tu conocimiento{' '}
            <br className="hidden md:block" data-id="element-228" data-mp="2909wi1w5mv1c" />
            hacia un nivel profesional y consciente.
          </p>

          <div className="flex items-center justify-center gap-4 mt-8" data-id="element-229" data-mp="2dzmmuwm8ik8o">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#d4af37]/40" data-id="element-230" data-mp="3kf5osqnq4v0d" />
            <span className="text-[#b8865c] font-bold tracking-widest text-sm uppercase" data-id="element-231" data-mp="3khjesl3jrh12">
              ¡Gratis solo por hoy!
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#d4af37]/40" data-id="element-232" data-mp="152nzpcjby5uj" />
          </div>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12" data-id="element-233" data-mp="1a17jp7ed57k9">
          {[{
          url: 'https://cdn.magicpatterns.com/uploads/fgqNtD6H87Jo2uj5hHAQGL/Bono_1_(1).webp',
          title: 'Roll on Naturales',
          desc: 'Aprendé a crear mezclas terapéuticas en formato práctico para llevar con vos siempre.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/79hGEExphNRcr822ac9pgU/Bono_2_(1).webp',
          title: 'Guía de Aromaterapia',
          desc: 'Los fundamentos esenciales para entender cómo los aromas impactan en tus emociones.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/5uo52XdvPnzR5eY37fsXFG/Bono_3_(1).webp',
          title: 'Aromas y Cristales',
          desc: 'Una combinación mágica para potenciar la energía de tus creaciones con gemas naturales.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/ajRptQpyRv7o5cAPw4CwnE/Bono_4_(1).webp',
          title: 'Cajas Aromáticas',
          desc: 'Ideas creativas para presentar tus productos y convertirlos en el regalo perfecto.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/xndhTrcoWqCmFky1Qcog2N/Bono_5_(1).webp',
          title: 'Gráfica para Cajas',
          desc: 'Recursos visuales listos para usar que le darán un toque profesional a tu marca.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/dVz4Q4J3GD67hns2Nvqy2B/Bono_6.webp',
          title: 'Guía de Costos',
          desc: 'La herramienta clave para ponerle precio a tu trabajo y hacer crecer tu proyecto.'
        }].map((bono, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="group cursor-pointer flex flex-col" onClick={handleScrollToPurchase} data-id="element-234" data-mp="xl7yx44zz1g4" data-mpi={i}>
              <div className="relative aspect-[4/5] overflow-hidden rounded-3xl shadow-2xl border border-rose-50/50 mb-6" data-id="element-235" data-mp="20e3qaa2egiqi">
                <div className="absolute top-4 right-4 z-20" data-id="element-236" data-mp="24dktewuct3si">
                  <div style={{
                fontFamily: "'Playfair Display', serif"
              }} className="bg-[#b76e79] text-white px-4 py-1 rounded-full text-xs font-bold tracking-[0.15em] shadow-lg border border-white/20 backdrop-blur-sm" data-id="element-237" data-mp="1qdjkcu3xqq1y">
                    GRATIS
                  </div>
                </div>
                <img src={bono.url} alt={bono.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out" data-id="element-238" data-mp="ce891548wcvg" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-239" data-mp="lewnu21gqh9e" />
              </div>

              <div className="px-2" data-id="element-240" data-mp="3owoglinfb58n">
                <h4 style={{
              fontFamily: "'Playfair Display', serif"
            }} className="text-xl md:text-2xl font-bold text-gray-800 mb-2 group-hover:text-[#b8865c] transition-colors duration-300 flex items-center gap-3" data-id="element-241" data-mp="3kv31rrrbwgvt">
                  <span className="w-8 h-px bg-[#d4af37]/40" data-id="element-242" data-mp="365f5x61x2afh" />
                  {bono.title}
                </h4>
                <p style={{
              fontFamily: "'Cormorant Garamond', serif"
            }} className="text-gray-600 text-lg leading-relaxed italic font-light" data-id="element-243" data-mp="315sqqr2j2k41">
                  {bono.desc}
                </p>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Community Creations Marquee */}
      <section className="mb-20 relative z-10 overflow-hidden" data-id="element-244" data-mp="3tj8pvdvmjcu0">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10 px-4" data-id="element-245" data-mp="1f671w61cezq">
          <span className="inline-block px-4 py-1.5 rounded-full bg-rose-50 border border-rose-100 text-[#b76e79] text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-5" data-id="element-246" data-mp="gbnisoi4zvmt">
            Nuestra comunidad
          </span>
          <h2 style={{
          fontFamily: "'Playfair Display', serif"
        }} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 leading-tight" data-id="element-247" data-mp="2i4bnl2afir4p">
            Mujeres que ya están{' '}
            <span className="gold-deep-text italic" data-id="element-248" data-mp="ufmgg4s557p8">creando</span>
          </h2>
          <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="text-lg md:text-xl text-gray-600 italic max-w-2xl mx-auto" data-id="element-249" data-mp="qg3x3dfyx4oi">
            Ellas adquirieron la guía y hoy dan vida a sus propias creaciones
            aromáticas.
          </p>
          <div className="flex items-center justify-center gap-3 mt-6" data-id="element-250" data-mp="1e3c4q20vcptp">
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-[#d4af37]/40 to-[#d4af37]/80" data-id="element-251" data-mp="194scjjvomhsw" />
            <svg className="w-4 h-4 text-[#d4af37]" viewBox="0 0 16 16" fill="currentColor" data-id="element-252" data-mp="1470nqyqc3bsx">
              <circle cx="8" cy="8" r="2" data-id="element-253" data-mp="3owyf32j900wa" />
              <path d="M8 0 L8.5 6 L8 8 L7.5 6 Z M8 16 L8.5 10 L8 8 L7.5 10 Z M0 8 L6 7.5 L8 8 L6 8.5 Z M16 8 L10 7.5 L8 8 L10 8.5 Z" opacity="0.8" data-id="element-254" data-mp="3ovwsq1ws7rz9" />
            </svg>
            <div className="h-px w-12 bg-gradient-to-l from-transparent via-[#d4af37]/40 to-[#d4af37]/80" data-id="element-255" data-mp="3vbngy5trniii" />
          </div>
        </motion.div>

        <div className="relative w-full" data-id="element-256" data-mp="3mcjv4u37sp0b">
          {/* Edge fade masks */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 z-10 pointer-events-none bg-gradient-to-r from-[#fdf6ee] to-transparent" data-id="element-257" data-mp="37o5mg2j4a6bu" />
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 z-10 pointer-events-none bg-gradient-to-l from-[#fdf6ee] to-transparent" data-id="element-258" data-mp="3cfnm8n9em0v8" />

          <motion.div className="flex gap-6 w-max" animate={{
          x: ['-50%', '0%']
        }} transition={{
          duration: 35,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-259" data-mp="2ylo0lp7djfpd">
            {['https://cdn.magicpatterns.com/uploads/6ERu2qgAVq1crQSW3SDGWa/ChatGPT_Image_30_may_2026,_02_10_09_a.m..webp', 'https://cdn.magicpatterns.com/uploads/f5TfWtXbYjRUrnsScdN1up/ChatGPT_Image_30_may_2026,_02_02_48_a.m..webp', 'https://cdn.magicpatterns.com/uploads/svBjM7HLFddf8sDHbacVuy/ChatGPT_Image_30_may_2026,_01_51_03_a.m..webp', 'https://cdn.magicpatterns.com/uploads/12VjNz3ewugQSBrYDZnfAF/ChatGPT_Image_30_may_2026,_01_49_31_a.m..webp', 'https://cdn.magicpatterns.com/uploads/jqxmiBMSZAPRNz7VinG2Q3/ChatGPT_Image_30_may_2026,_01_48_49_a.m..webp'].concat(['https://cdn.magicpatterns.com/uploads/6ERu2qgAVq1crQSW3SDGWa/ChatGPT_Image_30_may_2026,_02_10_09_a.m..webp', 'https://cdn.magicpatterns.com/uploads/f5TfWtXbYjRUrnsScdN1up/ChatGPT_Image_30_may_2026,_02_02_48_a.m..webp', 'https://cdn.magicpatterns.com/uploads/svBjM7HLFddf8sDHbacVuy/ChatGPT_Image_30_may_2026,_01_51_03_a.m..webp', 'https://cdn.magicpatterns.com/uploads/12VjNz3ewugQSBrYDZnfAF/ChatGPT_Image_30_may_2026,_01_49_31_a.m..webp', 'https://cdn.magicpatterns.com/uploads/jqxmiBMSZAPRNz7VinG2Q3/ChatGPT_Image_30_may_2026,_01_48_49_a.m..webp']).map((url, i) => <div key={i} className="relative shrink-0 w-56 h-72 md:w-64 md:h-80 rounded-[2rem] overflow-hidden shadow-xl" style={{
            border: '2px solid transparent',
            backgroundImage: 'linear-gradient(#fdf6ee, #fdf6ee), linear-gradient(135deg, #d4a574 0%, #e8b89a 50%, #b8865c 100%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }} data-id="element-260" data-mp="vzz9azviwcka" data-mpi={i}>
                  <img src={url} alt="Creación de la comunidad Esencia Áurica" className="w-full h-full object-cover" data-id="element-261" data-mp="3cmihi457c55z" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a0f11]/30 via-transparent to-transparent pointer-events-none" data-id="element-262" data-mp="3941ix8l7ujfb" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-1.5" data-id="element-263" data-mp="304xzg3i5020a">
                    <FlowerIcon className="w-4 h-4 text-white drop-shadow-md" strokeWidth={1.4} data-id="element-264" data-mp="2v75vuc84qrg9" />
                    <span style={{
                fontFamily: "'Cormorant Garamond', serif"
              }} className="text-white text-sm italic font-medium drop-shadow-md" data-id="element-265" data-mp="2q9mjxiywmqrh">
                      Esencia Áurica
                    </span>
                  </div>
                </div>)}
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}

      {/* FAQ Section */}

      {/* Guarantee Section */}

      {/* Final Call to Action Section */}
      <section id="compra" className="mb-16 scroll-mt-20 relative z-10" data-id="element-266" data-mp="1beuqdydf7sgy">
        <div id="purchase-section" className="text-white text-center py-4 px-4 rounded-t-[2.5rem] relative overflow-hidden" style={{
        background: 'linear-gradient(to right, #3d5a35, #5a7a4a, #3d5a35)',
        boxShadow: '0 -4px 20px rgba(61, 90, 53, 0.2)'
      }} data-id="element-267" data-mp="17f3u5f54kk51">
          <div className="relative z-10 flex items-center justify-center gap-3" data-id="element-268" data-mp="sqg05daxgih7">
            <div className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" data-id="element-269" data-mp="nrcw9w6ooiat" />
            <span className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold drop-shadow-sm" data-id="element-270" data-mp="12f7qxi494wqj">
              Oferta válida solo por hoy · {today}
            </span>
            <div className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" data-id="element-271" data-mp="xmy0m0z85unq" />
          </div>
        </div>
        <div className="bg-[#1a0f11] p-8 md:p-12 rounded-b-[2.5rem] shadow-2xl text-center relative overflow-hidden space-y-[16px]" data-id="element-272" data-mp="503mrmwwxtj8">
          {/* Subtle background texture */}
          <div className="absolute inset-0 opacity-10 pointer-events-none texture-grain" data-id="element-273" data-mp="2glcgzaewae27" />

          {/* Offer Selection Section */}
          <div className="relative z-10 max-w-2xl mx-auto mb-12 space-y-6 text-left" data-id="element-274" data-mp="27najh4ude3jo">
            <div className="text-center space-y-2 mb-10" data-id="element-275" data-mp="2bo2ua2iw4gi2">
              <p className="text-[#e2b4bd] text-xs md:text-sm font-medium tracking-[0.2em] uppercase" data-id="element-276" data-mp="2bom0wn33bigs">
                ✨ Elegí la alternativa que mejor se adapte a vos
              </p>
              <h3 style={{
              fontFamily: "'Playfair Display', serif"
            }} className="md:text-4xl font-bold text-white tracking-wide uppercase text-[18px]" data-id="element-277" data-mp="34bqdbd4iiz2l">
                Comenzá a crear desde hoy
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-278" data-mp="1ews6nc3ugnt2">
              {/* Option 1: Base */}
              <motion.div whileHover={{
              y: -5
            }} onClick={() => setSelectedOffer('base')} className={`relative p-6 md:p-8 rounded-[2.5rem] cursor-pointer transition-all duration-500 border-2 flex flex-col h-full ${selectedOffer === 'base' ? 'bg-white border-[#b76e79] shadow-[0_20px_50px_rgba(183,110,121,0.3)] scale-[1.02]' : 'bg-white/90 border-transparent hover:border-[#b76e79]/30 opacity-80'}`} data-id="element-279" data-mp="1jyhhaqn5mzgm">
                <div className="flex justify-between items-start mb-6" data-id="element-280" data-mp="32x5n9mjyjo09">
                  <div className="space-y-2" data-id="element-281" data-mp="2xvzw3t5y48su">
                    <h4 className="text-[#1a0f11] font-bold text-xl leading-tight pr-4" data-id="element-282" data-mp="2sxolhg7ttu9k">
                      {OFFERS.base.title}
                    </h4>
                    <div className="flex items-center gap-3" data-id="element-283" data-mp="2oxedxz4dyvsl">
                      <span className="text-gray-400 line-through text-sm font-medium" data-id="element-284" data-mp="r6toil1vvp19">
                        ${OFFERS.base.oldPrice}
                      </span>
                      <span className="text-[#00a650] font-black text-2xl" data-id="element-285" data-mp="n2lzemrik72i">
                        ${OFFERS.base.price}
                      </span>
                    </div>
                  </div>
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors duration-300 shrink-0 mt-1 ${selectedOffer === 'base' ? 'border-[#b76e79] bg-[#b76e79]/5' : 'border-gray-300'}`} data-id="element-286" data-mp="29zblteonncti">
                    {selectedOffer === 'base' && <motion.div layoutId="selector-dot" className="w-3.5 h-3.5 rounded-full bg-[#b76e79]" data-id="element-287" data-mp="1r2agfsr6vap1" />}
                  </div>
                </div>

                <p className="text-sm leading-relaxed mb-8 italic font-light text-[#000000]" data-id="element-288" data-mp="2041np21dnpl0">
                  {OFFERS.base.description}
                </p>

                <div className="space-y-4 mt-auto" data-id="element-289" data-mp="3bi5g43sbh5ny">
                  <p className="text-[#1a0f11] font-bold text-[10px] uppercase tracking-[0.15em] border-b border-gray-100 pb-2" data-id="element-290" data-mp="3bgkiu6emb8me">
                    Incluye:
                  </p>
                  <div className="space-y-2.5" data-id="element-291" data-mp="353gl9kg7uob8">
                    {OFFERS.base.includes.map((item, i) => <div key={i} className="flex items-center gap-3 text-gray-700 text-xs" data-id="element-292" data-mp="3j3hm4zwhdvqp" data-mpi={i}>
                        <CheckCircleIcon className="w-4 h-4 text-[#b76e79] shrink-0" data-id="element-293" data-mp="3szcb0crasn1x" />
                        <span className="font-medium" data-id="element-294" data-mp="3o1j5llxtjx2p">{item}</span>
                      </div>)}
                  </div>
                </div>
              </motion.div>

              {/* Option 2: Premium */}
              <motion.div whileHover={{
              y: -5
            }} onClick={() => setSelectedOffer('premium')} className={`relative p-6 md:p-8 rounded-[2.5rem] cursor-pointer transition-all duration-500 border-2 flex flex-col h-full ${selectedOffer === 'premium' ? 'bg-white border-[#b76e79] shadow-[0_25px_60px_rgba(183,110,121,0.4)] scale-[1.05]' : 'bg-white/90 border-transparent hover:border-[#b76e79]/30 opacity-80'}`} data-id="element-295" data-mp="1mhpid3exbgx">
                {/* Badge */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#b76e79] to-[#e2b4bd] text-white px-6 py-1.5 rounded-full text-[10px] font-black tracking-[0.2em] shadow-xl whitespace-nowrap z-20 border border-white/20" data-id="element-296" data-mp="l9b17srjhx7q">
                  ⭐ MÁS ELEGIDO
                </div>

                <div className="flex justify-between items-start mb-6" data-id="element-297" data-mp="g9yi9j3shaaf">
                  <div className="space-y-2" data-id="element-298" data-mp="1ktulme86dux">
                    <h4 className="text-[#1a0f11] font-bold text-xl leading-tight pr-4" data-id="element-299" data-mp="6i2fhuqrcvzv">
                      {OFFERS.premium.title}
                    </h4>
                    <div className="flex items-center gap-3" data-id="element-300" data-mp="3ooqinf28z92w">
                      <span className="text-gray-400 line-through text-sm font-medium" data-id="element-301" data-mp="3tm06iq9zncom">
                        ${OFFERS.premium.oldPrice}
                      </span>
                      <span className="text-[#00a650] font-black text-2xl" data-id="element-302" data-mp="1ugd094j1f8uf">
                        ${OFFERS.premium.price}
                      </span>
                    </div>
                  </div>
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors duration-300 shrink-0 mt-1 ${selectedOffer === 'premium' ? 'border-[#b76e79] bg-[#b76e79]/5' : 'border-gray-300'}`} data-id="element-303" data-mp="1lfoko5dzx0ff">
                    {selectedOffer === 'premium' && <motion.div layoutId="selector-dot" className="w-3.5 h-3.5 rounded-full bg-[#b76e79]" data-id="element-304" data-mp="293y2yxc0361t" />}
                  </div>
                </div>

                <p className="text-sm leading-relaxed mb-8 italic font-light text-[#000000]" data-id="element-305" data-mp="xnrcco9r8qj2">
                  {'Acced\xE9 al universo completo de la creaci\xF3n natural. Aprend\xE9 a elaborar perfumes naturales, Agua Florida, brumas \xE1uricas, skincare, cremas, maquillaje y productos capilares desde casa con 2 formaciones premium y 14 bonos exclusivos.'}
                </p>

                <div className="space-y-4 mt-auto" data-id="element-306" data-mp="3i595lpbsy7nn">
                  <p className="text-[#1a0f11] font-bold text-[10px] uppercase tracking-[0.15em] border-b border-gray-100 pb-2" data-id="element-307" data-mp="3i72bv3ii6pv4">
                    Incluye:
                  </p>
                  <div className="space-y-2.5" data-id="element-308" data-mp="3s2ma3wkji0n1">
                    {OFFERS.premium.includes.map((item, i) => <div key={i} className="flex items-center gap-3 text-gray-700 text-xs" data-id="element-309" data-mp="xvwl63ffik2" data-mpi={i}>
                        <CheckCircleIcon className="w-4 h-4 text-[#b76e79] shrink-0" data-id="element-310" data-mp="22p92nyh4rwhp" />
                        <span className="font-medium" data-id="element-311" data-mp="26q2l4cbt0j37">{item}</span>
                      </div>)}
                  </div>
                </div>

                <div className="mt-8 pt-4 border-t border-gray-100" data-id="element-312" data-mp="1ns94juu0qw2r">
                  <p className="text-[#b76e79] font-black text-xs text-center tracking-tight" data-id="element-313" data-mp="1iu7ozjvsxew1">
                    💜 Ahorrás más de $70.000 comprando el pack completo.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          <div className="space-y-3 relative z-10 pt-4" data-id="element-314" data-mp="14upucnvtr7er">
            <p className="text-rose-200/60 line-through text-xl font-light tracking-widest" data-id="element-315" data-mp="14xc1imeszkwg">
              Antes ${currentOffer.oldPrice} ARS
            </p>
            <div className="flex flex-col items-center" data-id="element-316" data-mp="1dwz4vdr3f0s5">
              <span className="text-rose-100/80 text-sm uppercase tracking-[0.3em] mb-1" data-id="element-317" data-mp="1dtjqye95nxsb">
                Inversión única
              </span>
              <p style={{
              fontFamily: "'Playfair Display', serif"
            }} className="text-6xl md:text-8xl font-bold text-white drop-shadow-[0_2px_10px_rgba(255,255,255,0.2)]" data-id="element-318" data-mp="2pa1lu9x3tscu">
                ${currentOffer.price}{' '}
                <span className="text-2xl md:text-3xl font-light opacity-80" data-id="element-319" data-mp="2wvna27g2nlaq">
                  ARS
                </span>
              </p>
            </div>
          </div>

          <div className="inline-block relative z-10" data-id="element-320" data-mp="2ntnr39lhy755">
            <div className="bg-gradient-to-r from-[#b76e79]/20 to-[#e2b4bd]/20 text-rose-100 px-4 py-1.5 rounded-full flex items-center gap-2 border border-[#b76e79]/40 backdrop-blur-sm" data-id="element-321" data-mp="2nu5y919p65ru">
              <CreditCardIcon className="w-3.5 h-3.5 text-[#e2b4bd]" data-id="element-322" data-mp="3gi1s7y7vtjkx" />
              <span className="font-medium tracking-wide text-[10px] md:text-xs whitespace-nowrap" data-id="element-323" data-mp="31ugny0z9tebf">
                Hasta 3 cuotas sin interés de $
                {selectedOffer === 'premium' ? '9.996' : '6.663'}
              </span>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl p-4 border border-white/10 grid grid-cols-2 gap-4 items-center relative z-10 max-w-lg mx-auto" data-id="element-324" data-mp="33olv7vu6c68">
            <div className="flex flex-col items-center gap-1 border-r border-white/10" data-id="element-325" data-mp="1ys1zvglzyy8z">
              <div className="flex items-center gap-2" data-id="element-326" data-mp="23klpjbxylju5">
                <div className="w-1.5 h-1.5 bg-[#e2b4bd] rounded-full animate-pulse shadow-[0_0_6px_#e2b4bd]" data-id="element-327" data-mp="23leklfnk2kwg"></div>
                <span className="text-rose-100/70 font-semibold text-[8px] md:text-[9px] uppercase tracking-[0.15em]" data-id="element-328" data-mp="1tth9sa0gluy9">
                  Explorando
                </span>
              </div>
              <span className="text-2xl font-bold text-white" data-id="element-329" data-mp="2mifsb88huz43">
                {viewers}{' '}
                <span className="text-xs font-light opacity-50 italic" data-id="element-330" data-mp="28in8lwvdxj5m">
                  mujeres
                </span>
              </span>
            </div>

            <div className="flex flex-col items-center gap-1" data-id="element-331" data-mp="2dg6k4p0p7htv">
              <div className="flex items-center gap-2" data-id="element-332" data-mp="x2yk312hgc2p">
                <TicketIcon className="w-3.5 h-3.5 text-[#b76e79]" data-id="element-333" data-mp="x5kt7550uzn1" />
                <span className="text-rose-100/70 font-semibold text-[8px] md:text-[9px] uppercase tracking-[0.15em]" data-id="element-334" data-mp="123cgtmymvzlf">
                  Cupos
                </span>
              </div>
              <div className="flex flex-col items-center" data-id="element-335" data-mp="pixebusk2bmb">
                <span className="text-2xl font-bold text-[#b76e79]" data-id="element-336" data-mp="13jqaejyi9erj">
                  {spots}
                </span>
                <p className="text-[7px] text-rose-200/40 italic" data-id="element-337" data-mp="yk5c8br5tafv">
                  Último acceso hace 5m
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-b from-white/5 to-transparent p-3 rounded-xl border border-white/5 relative z-10 max-w-[240px] mx-auto" data-id="element-338" data-mp="5vzqpmsa7hez">
            <div className="text-2xl font-bold text-white mb-0.5 tracking-tighter" data-id="element-339" data-mp="klxgg75v0f6a">
              <AnimatedCounter target={joinedCount} showPlus={false} data-id="element-340" data-mp="ftvxk2e6rq7a" />
            </div>
            <div className="text-rose-100/60 text-[8px] uppercase tracking-[0.2em] font-medium leading-tight" data-id="element-341" data-mp="3o9fnarfneqhl">
              Almas creativas ya se sumaron
            </div>
          </div>

          {/* Countdown Timer & Progress Bar */}
          <div className="relative z-10 max-w-sm mx-auto space-y-3 py-2" data-id="element-342" data-mp="1n7842t1kk0hj">
            <div className="flex justify-between items-end text-[10px] uppercase tracking-[0.2em] font-bold" data-id="element-343" data-mp="1s61ak1ifur87">
              <span className="text-rose-100/60" data-id="element-344" data-mp="1wyubeeasdff0">La oferta finaliza en</span>
              <span className="text-[#e2b4bd] text-sm tabular-nums" data-id="element-345" data-mp="20zo2fgjizac4">
                {formatTime(timeLeft)}
              </span>
            </div>
            <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden border border-white/5" data-id="element-346" data-mp="14aq3dm4qsq95">
              <motion.div className="h-full bg-gradient-to-r from-[#b76e79] to-[#e2b4bd]" initial={{
              width: '100%'
            }} animate={{
              width: `${timeLeft / 900 * 100}%`
            }} transition={{
              duration: 1,
              ease: 'linear'
            }} data-id="element-347" data-mp="19a2rea7co3db" />
            </div>
          </div>

          <motion.a href={currentOffer.link} initial={{
          scale: 1
        }} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} animate={{
          boxShadow: ['0 0 0 0 rgba(74, 222, 128, 0)', '0 0 0 15px rgba(74, 222, 128, 0.1)', '0 0 0 0 rgba(74, 222, 128, 0)']
        }} transition={{
          boxShadow: {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }} className="relative block w-full overflow-hidden group rounded-2xl" data-id="element-348" data-mp="33s09x07pvt5u">
            <div className="absolute inset-0 bg-gradient-to-r from-[#22c55e] via-[#4ade80] to-[#22c55e] transition-all duration-500 group-hover:opacity-100" style={{
            boxShadow: '0 10px 40px rgba(34, 197, 94, 0.4)'
          }} data-id="element-349" data-mp="273u6ggmj76t5" />

            {/* Shine animation */}
            <motion.div className="absolute inset-0 w-full h-full" style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
            transform: 'translateX(-100%)'
          }} animate={{
            transform: ['translateX(-100%)', 'translateX(200%)']
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut',
            repeatDelay: 1
          }} data-id="element-350" data-mp="hcxm8wk7nlap" />

            <div className="relative py-6 px-4 flex items-center justify-center gap-4 border border-white/20 rounded-2xl" data-id="element-351" data-mp="3kscv3ugurou9">
              <span className="text-white text-xl md:text-2xl font-black tracking-[0.08em] uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" data-id="element-352" data-mp="36uobsr2foprb">
                SÍ, QUIERO MI GUÍA AHORA
              </span>
              <ArrowRightIcon className="w-7 h-7 text-white group-hover:translate-x-2 transition-transform drop-shadow-md" data-id="element-353" data-mp="2s76be4vwmhap" />
            </div>
          </motion.a>

          <p className="text-[10px] text-rose-200/30 uppercase tracking-[0.2em] relative z-10" data-id="element-354" data-mp="317uiefmvrhe3">
            Acceso inmediato tras la compra · Pago 100% seguro
          </p>

          {/* MercadoLíder Platinum Section */}
          <div className="relative z-10 max-w-md mx-auto bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-left font-sans" data-id="element-355" data-mp="3b47ka0skx1jx">
            <div className="flex items-center gap-3 mb-4" data-id="element-356" data-mp="364l6nq8u9jal">
              <div className="w-10 h-10 bg-[#00a650]/10 rounded-full flex items-center justify-center" data-id="element-357" data-mp="3ku0j017mhm6r">
                <MedalIcon className="w-6 h-6 text-[#00a650]" data-id="element-358" data-mp="3fxju34sln3sm" />
              </div>
              <div data-id="element-359" data-mp="3tx1oq0sktb9w">
                <h4 className="text-[#00a650] font-bold text-base leading-tight" data-id="element-360" data-mp="3twjiqcg43ios">
                  MercadoLíder Platinum
                </h4>
                <p className="text-gray-500 text-xs" data-id="element-361" data-mp="2mjhivsypjad9">
                  ¡Es uno de los mejores del sitio!
                </p>
              </div>
            </div>

            {/* ML Reputation Bar */}
            <div className="flex gap-1 mb-4" data-id="element-362" data-mp="2vk76489wjsfd">
              <div className="h-2 flex-1 bg-[#fff159] rounded-l-full opacity-30" data-id="element-363" data-mp="2vje059irp6ya" />
              <div className="h-2 flex-1 bg-[#fff159] opacity-50" data-id="element-364" data-mp="30bxngy79bgga" />
              <div className="h-2 flex-1 bg-[#fff159]" data-id="element-365" data-mp="35a8xpa1grsz1" />
              <div className="h-2 flex-1 bg-[#86d58d]" data-id="element-366" data-mp="359h424rfg5be" />
              <div className="h-2 flex-1 bg-[#00a650] rounded-r-full" data-id="element-367" data-mp="3a8k57e4adre3" />
            </div>

            <div className="space-y-3" data-id="element-368" data-mp="3f8g1ssanmgjm">
              <div className="flex items-center gap-3" data-id="element-369" data-mp="3f9hknixww7bw">
                <div className="text-gray-800 font-bold text-lg leading-none" data-id="element-370" data-mp="3j9i1194kmsll">
                  1010
                </div>
                <div className="text-gray-500 text-xs leading-tight" data-id="element-371" data-mp="1kv6zplkftxbe">
                  Ventas en los <br data-id="element-372" data-mp="1ov7fb9j9wek1" /> últimos 60 días
                </div>
              </div>

              <div className="h-px bg-gray-100 w-full" data-id="element-373" data-mp="1hby2f6bk5xq8" />

              <div className="flex items-center gap-3" data-id="element-374" data-mp="1hdsrmwtv4c04">
                <CheckCircleIcon className="w-4 h-4 text-[#00a650]" data-id="element-375" data-mp="1ckzjs033knjt" />
                <span className="text-gray-700 text-sm" data-id="element-376" data-mp="1cjojwbcrvzxz">
                  Brinda buena atención
                </span>
              </div>

              <div className="flex items-center gap-3" data-id="element-377" data-mp="13lmbhaqpm50o">
                <CheckCircleIcon className="w-4 h-4 text-[#00a650]" data-id="element-378" data-mp="13j02d6o67hgc" />
                <span className="text-gray-700 text-sm" data-id="element-379" data-mp="20ak7ewa748zf">
                  Despacha sus productos a tiempo
                </span>
              </div>

              <div className="flex items-center gap-3 pt-1" data-id="element-380" data-mp="1ra5kjshl2ltt">
                <ShieldCheckIcon className="w-4 h-4 text-blue-500" data-id="element-381" data-mp="1rci9iplrdwgd" />
                <span className="text-gray-800 font-semibold text-sm" data-id="element-382" data-mp="1maj316uq2jf7">
                  Compra 100% Segura
                </span>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="relative z-10 pt-4 space-y-4" data-id="element-383" data-mp="aukrmwc2nlat">
            <div className="flex justify-center" data-id="element-384" data-mp="2axvn7idnfspq"></div>
            <div className="grid grid-cols-2 gap-4 items-center max-w-md mx-auto" data-id="element-385" data-mp="2b0htzfspu46w">
              <div className="flex justify-center" data-id="element-386" data-mp="2f1jv62670qbi">
                <img src="https://cdn.magicpatterns.com/uploads/4LARTWYp6jZPiWmfpjYGfe/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra segura" className="w-full max-w-[160px] h-auto bg-white/95 rounded-lg p-2" data-id="element-387" data-mp="210qzhe45np6t" />
              </div>
              <div className="flex justify-center" data-id="element-388" data-mp="2ypsncc7qkwb0">
                <img src="https://cdn.magicpatterns.com/uploads/m7ejn7rd1hwj94ZWXeRhyN/capture_260410_163646.png" alt="Métodos de pago seguros" className="w-full max-w-[180px] h-auto bg-white/95 rounded-lg p-2" data-id="element-389" data-mp="2jy1z4ztp8kms" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="comunidad" className="mb-24 relative z-10" data-id="element-390" data-mp="15ygm4pzlqbur">
        <div className="bg-white/60 backdrop-blur-md p-8 rounded-[2rem] shadow-xl border border-rose-100 relative overflow-hidden" data-id="element-391" data-mp="110f2o3y9kair">
          <div className="absolute inset-0 texture-grain opacity-10 pointer-events-none" data-id="element-392" data-mp="1es24ejaz6b8x" />

          {/* Contact Box */}
          <div className="mb-12 p-8 rounded-[2rem] bg-rose-50/40 backdrop-blur-sm border border-rose-100 text-center space-y-6 relative z-10" data-id="element-393" data-mp="1aqql0vip2m96">
            <p style={{
            fontFamily: "'Playfair Display', serif"
          }} className="text-xl md:text-2xl text-gray-800 font-medium" data-id="element-394" data-mp="i1jd55xcokb9">
              ¿Tenés alguna duda?
            </p>
            <p style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} className="text-lg text-gray-600 italic" data-id="element-395" data-mp="r1g79n2iebdp">
              Hacé click para contactarte con nosotros y te ayudaremos a la
              brevedad.
            </p>
            <motion.a href="mailto:soportebooknova@gmail.com" whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#b76e79] to-[#e2b4bd] text-white font-bold tracking-wide shadow-lg hover:shadow-rose-200/50 transition-all" data-id="element-396" data-mp="2h4qumbz23d3q">
              Contactar por Email
              <ArrowRightIcon className="w-5 h-5" data-id="element-397" data-mp="2vsls05o0e5n5" />
            </motion.a>
          </div>

          <h2 className="text-3xl font-bold mb-8 text-center relative z-10" data-id="element-398" data-mp="2r1lvh1isxpl6">
            Comunidad y Reseñas
          </h2>
          <div className="mb-12 space-y-4" data-id="element-399" data-mp="2m298mgrxkiil">
            <div className="flex gap-2 mb-2" data-id="element-400" data-mp="3jig0kb1ap81l">
              {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setNewRating(star)} className={`transition-colors ${newRating >= star ? 'text-yellow-400' : 'text-gray-300'}`} data-id="element-401" data-mp="3jk9bw3musgfx">
                  <StarIcon className={`w-8 h-8 ${newRating >= star ? 'fill-current' : ''}`} data-id="element-402" data-mp="3r15s0nk5iicm" />
                </button>)}
            </div>
            <textarea className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yellow-400 outline-none min-h-[120px] bg-white" placeholder="Escribí tu reseña aquí..." value={newComment} onChange={e => setNewComment(e.target.value)} data-id="element-403" data-mp="4va8lv6ghb92" />
            <Button className="w-full md:w-auto bg-gray-900 text-white px-8 py-3 rounded-xl font-bold" onClick={handleAddComment} data-id="element-404" data-mp="ingnzf1sbe7g">
              Publicar comentario
            </Button>
          </div>

          <div className="space-y-8" data-id="element-405" data-mp="siieae7sqmbm">
            {comments.slice(0, visibleCommentsCount).map(comment => <div key={comment.id} className="flex gap-4 border-b border-gray-100 pb-8" data-id="element-406" data-mp="2tmkmq346jqlk">
                <img src={comment.avatar} alt={comment.name} className="w-12 h-12 rounded-full bg-gray-100" data-id="element-407" data-mp="2ooj4tlihpxbo" />
                <div className="flex-1 space-y-2" data-id="element-408" data-mp="2xnmp2ste8irr">
                  <div className="flex justify-between items-start" data-id="element-409" data-mp="2xm1pupw4wbnk">
                    <div data-id="element-410" data-mp="3cbr67scssm8f">
                      <div className="font-bold" data-id="element-411" data-mp="3cf59r3x771hg">{comment.name}</div>
                      <div className="text-xs text-gray-400" data-id="element-412" data-mp="37d64tplt7wie">
                        {comment.date}
                      </div>
                    </div>
                    <div className="flex gap-0.5" data-id="element-413" data-mp="3hc5e9aeefw6g">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-3 h-3 ${i < comment.rating ? 'text-yellow-400 fill-current' : 'text-gray-200'}`} data-id="element-414" data-mp="1qzfy0b3b2u52" data-mpi={i} />)}
                    </div>
                  </div>
                  <p className="text-gray-700" data-id="element-415" data-mp="1gvrgv1epzqvr">{comment.text}</p>
                  <button className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors" data-id="element-416" data-mp="1gv99b8mlxq8j">
                    <HeartIcon className="w-4 h-4" data-id="element-417" data-mp="132jo36ewkpch" /> {comment.likes}
                  </button>
                </div>
              </div>)}
          </div>

          {visibleCommentsCount < comments.length && <div className="mt-12 text-center" data-id="element-418" data-mp="y51gpkgjai6l">
              <Button className="bg-white border-2 border-gray-200 text-gray-700 px-10 py-4 rounded-2xl font-bold hover:bg-gray-50 transition-colors" onClick={() => setVisibleCommentsCount(prev => Math.min(prev + 5, comments.length))} data-id="element-419" data-mp="y6uleu7l6sby">
                Cargar 5 comentarios más
              </Button>
            </div>}
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="mb-24 relative z-10 px-4" data-id="element-420" data-mp="jfleajl1v235">
        <Lavender className="top-1/2 -left-10 opacity-20" scale={1.3} rotate={-15} delay={1.5} data-id="element-421" data-mp="eh0aka6la09y" />
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-422" data-mp="9k9x7um65is8">
          <h2 style={{
          fontFamily: "'Playfair Display', serif"
        }} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-423" data-mp="3ruvc3fhwpl54">
            Preguntas <span className="gold-deep-text italic" data-id="element-424" data-mp="3hztnqlvggn3l">frecuentes</span>
          </h2>
          <div className="h-px w-16 bg-[#d4af37]/40 mx-auto" data-id="element-425" data-mp="3dvvomb64rcaj" />
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-4" data-id="element-426" data-mp="38ydewikagt1h">
          {[{
          q: '¿Necesito conocimientos previos?',
          a: 'No, la guía está diseñada para llevarte paso a paso, desde lo más básico hasta la creación de tus propios productos profesionales.'
        }, {
          q: '¿Cómo recibo la guía?',
          a: 'Una vez realizado el pago, recibirás un correo electrónico con el acceso inmediato para descargar la guía y todos los bonos de regalo.'
        }, {
          q: '¿Qué materiales necesito para empezar?',
          a: 'En la guía encontrarás una lista detallada, pero lo principal son aceites esenciales, alcohol de cereal y envases de vidrio. Son fáciles de conseguir.'
        }, {
          q: '¿Sirve para emprender?',
          a: '¡Totalmente! Incluimos una sección específica para ayudarte a crear tu propia marca y una guía de costos para que tu proyecto sea rentable.'
        }, {
          q: '¿Tengo garantía de compra?',
          a: 'Sí, confiamos tanto en el valor de nuestro contenido que ofrecemos una garantía de satisfacción de 7 días.'
        }].map((item, i) => <FaqItem key={i} question={item.q} answer={item.a} index={i} data-id="element-427" data-mp="2333owb286it9" data-mpi={i} />)}
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="mb-24 relative z-10 px-4" data-id="element-428" data-mp="1z2kxgi48verl">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="max-w-2xl mx-auto p-8 md:p-12 rounded-[3rem] bg-gradient-to-br from-white/40 to-rose-50/30 border border-rose-100/60 shadow-xl text-center relative overflow-hidden" data-id="element-429" data-mp="1bbxlnlr7k4zr">
          {/* Decorative background glow */}
          <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-[#d4af37]/5 rounded-full blur-3xl" data-id="element-430" data-mp="2vtlp94bke62z" />

          <div className="relative z-10" data-id="element-431" data-mp="2z9pxozfn2b5i">
            <div className="w-16 h-16 bg-gradient-to-br from-[#d4a574] to-[#b8865c] rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" data-id="element-432" data-mp="2z8n6eqeem6p2">
              <ShieldCheckIcon className="w-8 h-8 text-white" data-id="element-433" data-mp="2pbijgocqi777" />
            </div>

            <h2 style={{
            fontFamily: "'Playfair Display', serif"
          }} className="text-2xl md:text-3xl font-bold text-gray-800 mb-6" data-id="element-434" data-mp="2pdmyo2g32sms">
              Garantía de{' '}
              <span className="gold-deep-text italic" data-id="element-435" data-mp="3mzsn17s6530f">satisfacción</span>
            </h2>

            <div className="space-y-4" data-id="element-436" data-mp="38ao3nn9n0sqq">
              <p style={{
              fontFamily: "'Cormorant Garamond', serif"
            }} className="text-xl text-gray-700 italic leading-relaxed" data-id="element-437" data-mp="389ld5gg88sbc">
                Confiamos plenamente en el valor de nuestra guía.
              </p>
              <p style={{
              fontFamily: "'Cormorant Garamond', serif"
            }} className="text-lg md:text-xl text-gray-600 leading-relaxed" data-id="element-438" data-mp="9lfrlchnvg0i">
                Si en <span className="font-bold text-[#b76e79]" data-id="element-439" data-mp="20vhoago9cgsr">7 días</span>{' '}
                sentís que no es para vos, te devolvemos el{' '}
                <span className="font-bold text-[#b76e79]" data-id="element-440" data-mp="1rwe6dg0tu7fu">
                  100% de tu dinero
                </span>
                .
              </p>
              <div className="pt-4" data-id="element-441" data-mp="2ollsap5cyika">
                <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold text-[#b8865c]" data-id="element-442" data-mp="2jnafq6nohtyd">
                  Sin preguntas · Sin vueltas · Tu inversión está protegida
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Success Modal */}
      <AnimatePresence data-id="element-443" data-mp="147339rw080yj">
        {showSuccessModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-id="element-444" data-mp="144gtrmpjzbdo">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white p-8 rounded-3xl shadow-2xl max-w-sm w-full text-center space-y-6" data-id="element-445" data-mp="3juyt7rds2qjz">
              <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto" data-id="element-446" data-mp="7dtwtyqdk87x">
                <CheckCircleIcon className="w-12 h-12" data-id="element-447" data-mp="bf5bxabmhb1v" />
              </div>
              <div className="space-y-2" data-id="element-448" data-mp="2968cer5g50l6">
                <h3 className="text-2xl font-bold" data-id="element-449" data-mp="297je8lfbzy9n">
                  ¡Gracias por tu comentario!
                </h3>
                <p className="text-gray-600" data-id="element-450" data-mp="nh8mqiprju14">
                  Tu opinión ayuda a la comunidad.
                </p>
              </div>
              <Button className="w-full bg-gray-900 text-white py-4 rounded-xl font-bold" onClick={() => setShowSuccessModal(false)} data-id="element-451" data-mp="iixa608335f7">
                Cerrar
              </Button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Recent Buyer Notification */}
      <AnimatePresence data-id="element-452" data-mp="16e00kcmjdgxs">
        {recentBuyer && <motion.div initial={{
        x: -100,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: -100,
        opacity: 0
      }} className="fixed bottom-6 left-6 z-40 bg-rose-50/80 backdrop-blur-md p-3 rounded-2xl shadow-xl border border-rose-100/50 flex items-center gap-3 max-w-[240px] cursor-pointer" onClick={handleScrollToPurchase} data-id="element-453" data-mp="11ht37887hdrg">
            <div className="w-10 h-10 bg-rose-100/50 rounded-full flex items-center justify-center shrink-0" data-id="element-454" data-mp="23tubi8tvhwzm">
              <UsersIcon className="w-5 h-5 text-[#b76e79]" data-id="element-455" data-mp="28rmcdschwtfy" />
            </div>
            <div data-id="element-456" data-mp="1tubua05ckpb9">
              <div className="text-[13px] font-bold text-gray-800 leading-tight" data-id="element-457" data-mp="1tuvddk6qhjqv">
                {recentBuyer.name} de {recentBuyer.city}
              </div>
              <div className="text-[11px] text-gray-500 leading-tight" data-id="element-458" data-mp="1l2jkzss27m89">
                Acaba de comprar Esencia Áurica 🌿
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const INITIAL_COMMENTS = [{
  id: 1,
  name: 'Romina G.',
  avatar: 'https://cdn.magicpatterns.com/uploads/t2kBtQkEaATGZkxuL5TAnk/image.png',
  date: 'hace 2 horas',
  rating: 5,
  text: 'Me llego super rápido el mail con todo, ya empece a leer y es hermoso. Gracias!',
  likes: 24
}, {
  id: 2,
  name: 'Estela M.',
  avatar: 'https://cdn.magicpatterns.com/uploads/kyMxRspKrBPJM914sUPkpu/image.png',
  date: 'hace 4 horas',
  rating: 5,
  text: 'Me encantó el libro digital, los estoy leyendo desde ayer y me gustó como se complementa con la aromaterapia',
  likes: 18
}, {
  id: 3,
  name: 'Julieta',
  avatar: 'https://cdn.magicpatterns.com/uploads/3usJuqgZWM4ULGThiy6Zk3/image.png',
  date: 'hace 5 horas',
  rating: 4,
  text: 'Muy bueno todo, me hubiera gustado mas recetas de perfumes amaderados pero igual esta re completo.',
  likes: 12
}, {
  id: 4,
  name: 'Carla',
  avatar: 'https://cdn.magicpatterns.com/uploads/qwQzdWdgyiEhZzEMV4tLrP/image.png',
  date: 'hace 8 horas',
  rating: 5,
  text: 'Es la guia q necesitaba para arrancar de una vez por todas. Re clara la explicacion.',
  likes: 31
}, {
  id: 5,
  name: 'Moni',
  avatar: 'https://images.unsplash.com/photo-1554151228-14d9def656e4?q=80&w=256&h=256&auto=format&fit=crop',
  date: 'hace 12 horas',
  rating: 5,
  text: 'Amo los aromas naturales y aca aprendi un monton de cosas q no sabia. Vale la pena.',
  likes: 15
}, ...Array.from({
  length: 25
}).map((_, i) => ({
  id: i + 6,
  name: `Usuario ${i + 6}`,
  avatar: `https://images.unsplash.com/photo-${['1438761681033-6461ffad8d80', '1544005313-94ddf0286df2', '1554151228-14d9def656e4', '1531746020798-e6953c6e8e04', '1506794778202-cad84cf45f1d', '1494790108377-be9c29b29330', '1517841905240-472988babdf9', '1534528741775-53994a69daeb', '1524504388940-b1c1722653e1', '1507081323142-f62f66224c9d'][i % 10]}?q=80&w=256&h=256&auto=format&fit=crop`,
  date: 'hace 1 día',
  rating: Math.random() > 0.1 ? 5 : 4,
  text: 'Excelente material, muy conforme con la compra.',
  likes: Math.floor(Math.random() * 10)
}))];